// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useState, type FC } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Label,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  FileStack,
  Database,
  Key,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react'
import { apiClient } from '@/app-shell/api/api-client'
import type { DatasourceConfigProps, OptixDocumentsSettings } from '../types'

// UMD Optix server environments
const OPTIX_SERVERS = [
  { id: 'optix-app1.osfa-dev-optix.aws.umd.edu', label: 'Development' },
  { id: 'optix-app1.osfa-qa-optix.aws.umd.edu', label: 'QA' },
  { id: 'optix-app1.osfa-optix.aws.umd.edu', label: 'Production' },
] as const

// Credential keys used by this datasource
const CREDENTIAL_KEYS = {
  optix: 'i9optixrmi',
  itx: 'i9optixoracleitx',
  ita: 'i9optixoracleita',
  itw: 'i9optixoracleitw',
} as const

interface CredentialStatusProps {
  credentialKey: string
  status: boolean | null | undefined
}

function CredentialStatus({ credentialKey, status }: CredentialStatusProps) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Key className="h-3 w-3" />
      <span>Credential key: {credentialKey}</span>
      {status === true && (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" aria-label="Configured" />
      )}
      {status === false && (
        <XCircle className="h-3.5 w-3.5 text-red-500" aria-label="Not configured" />
      )}
      {status === null && (
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-label="Could not check" />
      )}
    </div>
  )
}

/**
 * Configuration form for OptixDocuments datasource.
 * Configures connection settings for Optix document fetching.
 */
export const OptixDocumentsConfig: FC<
  DatasourceConfigProps<OptixDocumentsSettings> & {
    orgId?: string
    workspaceId?: string
  }
> = ({ config, onChange, orgId, workspaceId }) => {
  const [credentialStatus, setCredentialStatus] = useState<Record<string, boolean | null>>({})

  // Fetch credential status on mount
  useEffect(() => {
    if (!orgId || !workspaceId) return

    apiClient
      .get<{ credentials: Record<string, boolean | null> }>(
        `/org/${orgId}/ws/${workspaceId}/i9/credentials/status`
      )
      .then((res) => setCredentialStatus(res.data.credentials))
      .catch(() => setCredentialStatus({}))
  }, [orgId, workspaceId])

  // Optix RMI settings
  const optixUsername = config?.optixUsername ?? 'optxreviewi9dev'
  const optixServer = config?.optixServer ?? ''
  const optixScreenName = config?.optixScreenName ?? 'I9_RECORDS'
  const optixTableName = config?.optixTableName ?? 'I9'

  // Oracle ITX settings (Optix RDS for document listing)
  const oracleItxUsername = config?.oracleItxUsername ?? 'optix_uhr'
  const oracleItxDsn =
    config?.oracleItxDsn ?? 'rds-itxd19.osfa-dev-optix.aws.umd.edu:1521/itxd19'

  // Oracle ITA settings (HR transactional)
  const oracleItaUsername = config?.oracleItaUsername ?? 'docreview'
  const oracleItaDsn = config?.oracleItaDsn ?? 'sdc-itad10.umd.edu:1521/itad10_rw'

  // Oracle ITW settings (HR warehouse)
  const oracleItwUsername = config?.oracleItwUsername ?? 'docreview'
  const oracleItwDsn = config?.oracleItwDsn ?? 'sdc-itwd10.umd.edu:1521/itwd10_rw'

  // Document filtering
  const scanDateAfter = config?.scanDateAfter ?? ''
  const maxDocuments = config?.maxDocuments ?? 100
  const datasetKey = config?.datasetKey ?? 'i9_match'

  return (
    <div className="space-y-4">
      {/* Optix RMI Connection */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileStack className="h-4 w-4" />
            Optix RMI Connection
          </CardTitle>
          <CardDescription>PDF downloads via optix-java Lambda</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CredentialStatus
            credentialKey={CREDENTIAL_KEYS.optix}
            status={credentialStatus[CREDENTIAL_KEYS.optix]}
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="optix-server">Server</Label>
              <Select
                value={optixServer}
                onValueChange={(value) => onChange('optixServer', value)}
              >
                <SelectTrigger id="optix-server">
                  <SelectValue placeholder="Select Optix server" />
                </SelectTrigger>
                <SelectContent>
                  {OPTIX_SERVERS.map((server) => (
                    <SelectItem key={server.id} value={server.id}>
                      {server.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="optix-username">Username</Label>
              <Input
                id="optix-username"
                value={optixUsername}
                onChange={(e) => onChange('optixUsername', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="optix-screen">Screen Name</Label>
              <Input
                id="optix-screen"
                value={optixScreenName}
                onChange={(e) => onChange('optixScreenName', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="optix-table">Table Name</Label>
              <Input
                id="optix-table"
                value={optixTableName}
                onChange={(e) => onChange('optixTableName', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Oracle ITX Connection */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" />
            Oracle ITX Connection
          </CardTitle>
          <CardDescription>
            Optix RDS for fast document listing (D/Q/P for dev/qa/prod)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CredentialStatus
            credentialKey={CREDENTIAL_KEYS.itx}
            status={credentialStatus[CREDENTIAL_KEYS.itx]}
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="itx-username">Username</Label>
              <Input
                id="itx-username"
                value={oracleItxUsername}
                onChange={(e) => onChange('oracleItxUsername', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="itx-dsn">DSN</Label>
              <Input
                id="itx-dsn"
                value={oracleItxDsn}
                onChange={(e) => onChange('oracleItxDsn', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Oracle ITA Connection */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" />
            Oracle ITA Connection
          </CardTitle>
          <CardDescription>
            HR transactional data for employee matching (D/Q/P for dev/qa/prod)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CredentialStatus
            credentialKey={CREDENTIAL_KEYS.ita}
            status={credentialStatus[CREDENTIAL_KEYS.ita]}
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ita-username">Username</Label>
              <Input
                id="ita-username"
                value={oracleItaUsername}
                onChange={(e) => onChange('oracleItaUsername', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ita-dsn">DSN</Label>
              <Input
                id="ita-dsn"
                value={oracleItaDsn}
                onChange={(e) => onChange('oracleItaDsn', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Oracle ITW Connection */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" />
            Oracle ITW Connection
          </CardTitle>
          <CardDescription>
            HR warehouse data for employee matching (D/Q/P for dev/qa/prod)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CredentialStatus
            credentialKey={CREDENTIAL_KEYS.itw}
            status={credentialStatus[CREDENTIAL_KEYS.itw]}
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="itw-username">Username</Label>
              <Input
                id="itw-username"
                value={oracleItwUsername}
                onChange={(e) => onChange('oracleItwUsername', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="itw-dsn">DSN</Label>
              <Input
                id="itw-dsn"
                value={oracleItwDsn}
                onChange={(e) => onChange('oracleItwDsn', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Filtering & Output */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Document Filtering & Output</CardTitle>
          <CardDescription>
            Control which documents are fetched and output dataset key
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scan-date">Scan Date After</Label>
              <Input
                id="scan-date"
                type="date"
                value={scanDateAfter}
                onChange={(e) => onChange('scanDateAfter', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-docs">Max Documents</Label>
              <Input
                id="max-docs"
                type="number"
                min={1}
                max={10000}
                value={maxDocuments}
                onChange={(e) => onChange('maxDocuments', parseInt(e.target.value) || 100)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dataset-key">Dataset Key</Label>
              <Input
                id="dataset-key"
                value={datasetKey}
                onChange={(e) => onChange('datasetKey', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
